import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { finishLogin, forgotPasswordUrl, LoginError, passwordLogin, setNewPassword, takeReturnTo, useAuth } from '../lib/auth'
import { Alert, Loading, Sk } from '../ui/states'
import { SiteTitle } from '../ui/Layout'

/** What to tell the artist for each Cognito error name */
function loginMessage(error: unknown): string {
  const name = error instanceof LoginError ? error.message : ''
  if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') return 'That email and password don\'t match. Check them and try again.'
  if (name === 'InvalidPasswordException') return 'That password is too weak. Use at least 8 characters, with upper and lower case letters, a number and a symbol.'
  if (name === 'PasswordResetRequiredException') return 'This account needs a new password. Use Forgot your password below.'
  if (name === 'TooManyRequestsException' || name === 'LimitExceededException') return 'Too many tries. Wait a few minutes, then try again.'
  if (name === '') return 'Check your connection and try again.'
  return 'Something went wrong. Please try again.'
}

/** Email and password, plus a new password step if the account has a temporary one */
function PasswordForm() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fresh, setFresh] = useState('')
  const [session, setSession] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (session ? !fresh : !email.trim() || !password) {
      setFailure(session ? 'Enter a new password.' : 'Enter your email and password.')
      return
    }
    setBusy(true)
    setFailure('')
    try {
      if (session) {
        await setNewPassword(email.trim(), fresh, session)
      } else {
        const result = await passwordLogin(email.trim(), password)
        if (result.next === 'new-password') {
          setSession(result.session)
          setBusy(false)
          return
        }
      }
      navigate(takeReturnTo(), { replace: true })
    } catch (error) {
      setFailure(loginMessage(error))
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      {failure && <Alert title="Logging in didn't work">{failure}</Alert>}
      {session ? (
        <>
          <p className="lead">This is your first login, so choose a new password.</p>
          <div className="formfield">
            <label className="lbl" htmlFor="login-new">
              New password
            </label>
            <input id="login-new" className="field" type="password" autoComplete="new-password" value={fresh} onChange={(e) => setFresh(e.target.value)} disabled={busy} autoFocus />
          </div>
        </>
      ) : (
        <>
          <div className="formfield">
            <label className="lbl" htmlFor="login-email">
              Email
            </label>
            <input id="login-email" className="field" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
          </div>
          <div className="formfield">
            <label className="lbl" htmlFor="login-password">
              Password
            </label>
            <input id="login-password" className="field" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} />
          </div>
        </>
      )}
      <button type="submit" className="btn solid" disabled={busy}>
        {busy && <span className="spin" aria-hidden="true" />}
        {busy ? 'Logging in' : session ? 'Save and log in' : 'Log in'}
      </button>
      {!session && (
        <a href={forgotPasswordUrl()}>
          Forgot your password?
        </a>
      )}
    </form>
  )
}

/**
 * /login: the email and password form, and where the hosted "Forgot password"
 * pages come back to with ?code=.
 */
export function LoginPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { isArtist } = useAuth()
  const code = params.get('code')
  const errorText = params.get('error_description') ?? params.get('error')
  const [failed, setFailed] = useState(Boolean(errorText))
  const started = useRef(false)

  useEffect(() => {
    document.title = 'Log in · What a comical life'
  }, [])

  useEffect(() => {
    if (!code || started.current) return
    started.current = true
    finishLogin(code)
      .then((next) => navigate(next, { replace: true }))
      .catch(() => setFailed(true))
  }, [code, navigate])

  useEffect(() => {
    if (isArtist && !code) navigate('/artist/comics', { replace: true })
  }, [isArtist, code, navigate])

  const working = Boolean(code) && !failed

  return (
    <div className="login">
      <div className="login-inner">
        <SiteTitle />
        <div className="login-card">
          <div className="vstack gap-xs">
            <div className="eyebrow">Artist tools</div>
            <h1 className="h1 sm">
              Log in
            </h1>
          </div>
          {working ? (
            <Loading label="Logging you in">
              <Sk h={48} />
            </Loading>
          ) : (
            <>
              {failed && <Alert title="Logging in didn't work">Please try again.</Alert>}
              <PasswordForm />
            </>
          )}
        </div>
        <Link to="/">
          Back to the comics
        </Link>
      </div>
    </div>
  )
}
